// shared/campaign-clock.ts — PURE derived-campaign-clock math (S1 of
// plans/campaign-clock-client-handoff-2026-09-03.md). No DOM, no window, no fetch: importable by the
// browser runtime (campaign-time-init/campaign-clock-runtime.ts) AND by the Node parity test
// (tools/test/campaign-clock-parity.mjs), which byte-compares every function here against the PHP
// twins in includes/functions/campaigns/campaign_clock_inc.php + get_campaign_time_format_fs() +
// fetch_campaigns' SQL-joined calendar names across every calendar on staging.
//
// The clock model: the six campaigns.campaign_time_* columns are a CHECKPOINT; campaigns.campaign_clock_
// running_since (UTC 'Y-m-d H:i:s', null = paused) says whether and since when the clock is playing.
//   now = checkpoint + min(server_now − running_since, clock_stale_cap_seconds)
// Every reader derives this locally off the campaign blob; nobody polls for the time.

export interface CampaignClockCols {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
}

export interface CampaignCalendarMonth {
    month_order: number;
    month_name: string;
    month_season: string;
    days_in_month: number;
}

export interface CampaignCalendarWeekday {
    day_of_week: number;
    weekday_name: string;
}

/** computed.campaign.calendar — the lean embed build_campaign_arr writes (ft_campaign_calendar_embed). */
export interface CampaignCalendarEmbed {
    months: CampaignCalendarMonth[];
    weekdays: CampaignCalendarWeekday[];
    max_month_order: number;
    max_day_of_week: number;
    sum_days_in_year: number;
}

export interface CampaignClockNames {
    month_name: string;
    month_season: string;
    campaign_weekday_name: string;
}

/** The computed.campaign display strings get_campaign_time_format_fs() writes, plus the names. */
export interface CampaignClockFormatted extends CampaignClockNames {
    calendar_current_date_f: string;
    calendar_current_time_f: string;
    calendar_current_full_date_f: string;
    calendar_current_full_date_named_f: string;
    campaign_now_seconds: number;
}

const EMPTY_CALENDAR: CampaignCalendarEmbed = {
    months: [], weekdays: [], max_month_order: 0, max_day_of_week: 0, sum_days_in_year: 0,
};

function toInt(v: unknown): number {
    if (typeof v === 'number') return Number.isFinite(v) ? Math.trunc(v) : 0;
    if (typeof v === 'string') {
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
}

/** Bucket-first campaigns-column read (mirrors PHP campaign_field: `campaigns.<key>`, legacy `[0]` fallback). */
export function ftClockCampaignField(campaignArr: unknown, key: string): unknown {
    if (!campaignArr || typeof campaignArr !== 'object') return undefined;
    const arr = campaignArr as { campaigns?: Record<string, unknown>; [k: string]: unknown };
    const bucket = arr.campaigns;
    if (bucket && typeof bucket === 'object' && key in bucket) {
        const v = bucket[key];
        return v == null ? undefined : v;
    }
    const wrapped = arr[key];
    if (Array.isArray(wrapped)) return wrapped[0] == null ? undefined : wrapped[0];
    return wrapped == null ? undefined : wrapped;
}

export function ftClockColsFromArr(campaignArr: unknown): CampaignClockCols {
    return {
        year:   toInt(ftClockCampaignField(campaignArr, 'campaign_time_year')),
        month:  toInt(ftClockCampaignField(campaignArr, 'campaign_time_month')),
        day:    toInt(ftClockCampaignField(campaignArr, 'campaign_time_day')),
        hour:   toInt(ftClockCampaignField(campaignArr, 'campaign_time_hour')),
        minute: toInt(ftClockCampaignField(campaignArr, 'campaign_time_minute')),
        second: toInt(ftClockCampaignField(campaignArr, 'campaign_time_second')),
    };
}

/** Normalise a raw embed (JSON from PHP) into the typed shape; tolerant of a missing / partial embed. */
export function ftClockCalendarFromRaw(raw: unknown): CampaignCalendarEmbed {
    if (!raw || typeof raw !== 'object') return EMPTY_CALENDAR;
    const r = raw as Record<string, unknown>;
    const months: CampaignCalendarMonth[] = Array.isArray(r.months)
        ? r.months.map((m) => {
            const mm = (m ?? {}) as Record<string, unknown>;
            return {
                month_order: toInt(mm.month_order),
                month_name: String(mm.month_name ?? ''),
                month_season: String(mm.month_season ?? ''),
                days_in_month: toInt(mm.days_in_month),
            };
        })
        : [];
    const weekdays: CampaignCalendarWeekday[] = Array.isArray(r.weekdays)
        ? r.weekdays.map((w) => {
            const ww = (w ?? {}) as Record<string, unknown>;
            return { day_of_week: toInt(ww.day_of_week), weekday_name: String(ww.weekday_name ?? '') };
        })
        : [];
    return {
        months,
        weekdays,
        max_month_order: toInt(r.max_month_order),
        max_day_of_week: toInt(r.max_day_of_week),
        sum_days_in_year: toInt(r.sum_days_in_year),
    };
}

export function ftClockCalendarFromArr(campaignArr: unknown): CampaignCalendarEmbed {
    const computed = (campaignArr as { computed?: { campaign?: { calendar?: unknown } } } | null)?.computed?.campaign;
    return ftClockCalendarFromRaw(computed?.calendar);
}

/** days_in_month for a month order; 0 when the calendar has no such month (PHP ft_campaign_calendar_days_in_month). */
export function ftClockDaysInMonth(calendar: CampaignCalendarEmbed, monthOrder: number): number {
    for (const m of calendar.months) {
        if (m.month_order === monthOrder) return m.days_in_month;
    }
    return 0;
}

/**
 * fetch_campaigns' SQL-joined display names for an arbitrary date (PHP twin: ft_campaign_calendar_names):
 *   month_name / month_season = calendar_months row WHERE month_order = month (LEFT JOIN → '')
 *   campaign_weekday_name     = calendar_days.weekday_name WHERE day_of_week =
 *       GREATEST(1, (year * sum_days_in_year + days_before_month + day) % NULLIF(max_day_of_week, 0))
 */
export function ftClockCalendarNames(calendar: CampaignCalendarEmbed, year: number, month: number, day: number): CampaignClockNames {
    let month_name = '';
    let month_season = '';
    for (const m of calendar.months) {
        if (m.month_order === month) {
            month_name = m.month_name;
            month_season = m.month_season;
            break;
        }
    }
    let campaign_weekday_name = '';
    if (calendar.max_day_of_week > 0 && calendar.months.length > 0) {
        let before = 0;
        for (const m of calendar.months) {
            if (m.month_order < month) before += m.days_in_month;
        }
        let dow = (year * calendar.sum_days_in_year + before + day) % calendar.max_day_of_week;
        if (dow < 1) dow = 1;
        for (const w of calendar.weekdays) {
            if (w.day_of_week === dow) {
                campaign_weekday_name = w.weekday_name;
                break;
            }
        }
    }
    return { month_name, month_season, campaign_weekday_name };
}

/**
 * EXACT adder — PHP twin ft_campaign_time_add_seconds(): seconds → minutes → hours → days with the same
 * carries convert_seconds_to_campaign_time() applies; each day rolls through calendar_months by month_order,
 * a month past the last order wraps to 1 and bumps the year. Calendars with no months never roll (days
 * accumulate); a calendar whose months all read 0 days stops carrying after a bounded guard.
 */
export function ftCampaignTimeAddSeconds(cols: CampaignClockCols, calendar: CampaignCalendarEmbed, seconds: number): CampaignClockCols {
    seconds = Math.max(0, Math.trunc(seconds));
    let { year, month, day, hour, minute, second } = cols;
    if (seconds === 0) return { year, month, day, hour, minute, second };

    let daysAdd = Math.trunc(seconds / 86400);
    let rem = seconds % 86400;
    let hoursAdd = Math.trunc(rem / 3600);
    rem %= 3600;
    let minutesAdd = Math.trunc(rem / 60);
    const secondsAdd = rem % 60;

    if (secondsAdd > 0) {
        second += secondsAdd;
        while (second >= 60) { second -= 60; minutesAdd++; }
    }
    if (minutesAdd > 0) {
        minute += minutesAdd;
        while (minute >= 60) { minute -= 60; hoursAdd++; }
    }
    if (hoursAdd > 0) {
        hour += hoursAdd;
        while (hour >= 24) { hour -= 24; daysAdd++; }
    }
    if (daysAdd > 0) {
        const maxOrder = calendar.max_month_order;
        const hasMonths = calendar.months.length > 0;
        for (let d = 0; d < daysAdd; d++) {
            day++;
            if (!hasMonths) continue;
            let guard = 0;
            for (;;) {
                const dim = ftClockDaysInMonth(calendar, month);
                if (day <= dim) break;
                if (++guard > 2 * (maxOrder + 1)) break;
                day -= dim;
                month++;
                if (month > maxOrder) { month = 1; year++; }
            }
        }
    }
    return { year, month, day, hour, minute, second };
}

/**
 * Pull the clock BACK by `seconds` — the browser's twin of options/tools/campaigns/pull_time_inc.php (2026-09-30: the GM's
 * previous-turn round unwrap pulls one round, 6 s). Borrows second → minute → hour → day, and a day below 1 steps back through
 * calendar_months (adding the length of the month stepped INTO); below year 0 it clamps to the calendar epoch (0-1-1 00:00:00).
 */
export function ftCampaignTimeSubtractSeconds(cols: CampaignClockCols, calendar: CampaignCalendarEmbed, seconds: number): CampaignClockCols {
    seconds = Math.max(0, Math.trunc(seconds));
    let { year, month, day, hour, minute, second } = cols;
    if (seconds === 0) return { year, month, day, hour, minute, second };
    let daysSub = Math.trunc(seconds / 86400);
    let rem = seconds % 86400;
    let hoursSub = Math.trunc(rem / 3600);
    rem %= 3600;
    let minutesSub = Math.trunc(rem / 60);
    const secondsSub = rem % 60;
    if (secondsSub > 0) { second -= secondsSub; while (second < 0) { second += 60; minutesSub++; } }
    if (minutesSub > 0) { minute -= minutesSub; while (minute < 0) { minute += 60; hoursSub++; } }
    if (hoursSub > 0) { hour -= hoursSub; while (hour < 0) { hour += 24; daysSub++; } }
    if (daysSub > 0) {
        day -= daysSub;
        const maxOrder = calendar.max_month_order;
        while (day < 1 && calendar.months.length > 0) {
            month--;
            if (month < 1) {
                month = maxOrder;
                year--;
                if (year < 0) return { year: 0, month: 1, day: 1, hour: 0, minute: 0, second: 0 };
            }
            const dim = ftClockDaysInMonth(calendar, month);
            if (dim < 1) break; // a missing / zero calendar row never raises the day: stop instead of spinning (PHP's guard)
            day += dim;
        }
    }
    return { year, month, day, hour, minute, second };
}

/** PHP ordinal(): 1st 2nd 3rd 4th … 11th 12th 13th … 21st. */
export function ftClockOrdinal(n: number): string {
    const ends = ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'];
    const m100 = n % 100;
    if (m100 >= 11 && m100 <= 13) return `${n}th`;
    return `${n}${ends[((n % 10) + 10) % 10]}`;
}

/**
 * campaign_time_to_seconds(): absolute campaign seconds with the SAME (approximate) basis the server uses —
 * year × 12 × 30 days, then the real month lengths before this month, then day/hour/minute/second. The
 * activity bars subtract status end-seconds stamped on the same basis, so the basis must match, not be right.
 */
export function ftCampaignNowSeconds(cols: CampaignClockCols, calendar: CampaignCalendarEmbed): number {
    let total = cols.year * 12 * 30 * 24 * 3600;
    for (let m = 1; m < cols.month; m++) {
        total += ftClockDaysInMonth(calendar, m) * 24 * 3600;
    }
    total += (cols.day - 1) * 24 * 3600;
    total += cols.hour * 3600;
    total += cols.minute * 60;
    total += cols.second;
    return total;
}

function pad2(n: number): string {
    // PHP sprintf('%02d'): sign-aware zero padding to width 2.
    const s = String(Math.trunc(Math.abs(n)));
    const padded = s.length < 2 ? '0'.repeat(2 - s.length) + s : s;
    return n < 0 ? `-${padded}` : padded;
}

/** get_campaign_time_format_fs() — the four calendar_current_*_f strings + campaign_now_seconds, byte-for-byte. */
export function ftFormatCampaignTime(cols: CampaignClockCols, calendar: CampaignCalendarEmbed): CampaignClockFormatted {
    const names = ftClockCalendarNames(calendar, cols.year, cols.month, cols.day);
    const pm = cols.hour >= 12 ? 'PM' : 'AM';
    const hour12 = cols.hour === 0 ? 12 : (cols.hour > 12 ? cols.hour - 12 : cols.hour);
    const mm = pad2(cols.minute);
    const ss = pad2(cols.second);
    const date = `${cols.year}-${cols.month}-${cols.day}`;
    return {
        ...names,
        calendar_current_date_f: date,
        calendar_current_time_f: `${hour12}:${mm}:${ss} ${pm}`,
        calendar_current_full_date_f: `${date} | ${hour12}:${mm} ${pm}`,
        calendar_current_full_date_named_f: `${names.campaign_weekday_name}, ${names.month_name} ${ftClockOrdinal(cols.day)}, ${cols.year}`,
        campaign_now_seconds: ftCampaignNowSeconds(cols, calendar),
    };
}

/** 'Y-m-d H:i:s' (UTC) → epoch ms, or null. */
export function ftClockParseUtc(raw: unknown): number | null {
    if (typeof raw !== 'string') return null;
    const s = raw.trim();
    if (s === '' || s === '0000-00-00 00:00:00') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/.exec(s);
    if (!m) return null;
    const [y, mo, d, h, mi, se] = [+m[1]!, +m[2]!, +m[3]!, +m[4]!, +m[5]!, +m[6]!];
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || se > 59) return null;
    // Date.UTC maps years 0–99 to 1900–1999; set the full year explicitly (PHP does not share that rule).
    const dt = new Date(0);
    dt.setUTCFullYear(y, mo - 1, d);
    dt.setUTCHours(h, mi, se, 0);
    // Reject normalised overflow (e.g. Feb 30) so both parsers refuse the same strings.
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
    const t = dt.getTime();
    return Number.isFinite(t) ? t : null;
}

export interface CampaignClockBase {
    cols: CampaignClockCols;
    calendar: CampaignCalendarEmbed;
    /** Raw running_since string as the server stored it (sent back verbatim on a checkpoint). */
    runningSinceRaw: string | null;
    runningSinceMs: number | null;
    derived: boolean;
    staleCapSeconds: number;
}

/** Snapshot the clock inputs off a campaign blob (page-load seed or a campaign_update push). */
export function ftClockBaseFromArr(campaignArr: unknown): CampaignClockBase {
    const computed = (campaignArr as { computed?: { campaign?: Record<string, unknown> } } | null)?.computed?.campaign ?? {};
    const rawSince = ftClockCampaignField(campaignArr, 'campaign_clock_running_since');
    const sinceStr = typeof rawSince === 'string' && rawSince.trim() !== '' ? rawSince.trim() : null;
    const derived = computed.clock_derived === true || computed.clock_derived === 1 || computed.clock_derived === '1';
    const cap = toInt(computed.clock_stale_cap_seconds);
    return {
        cols: ftClockColsFromArr(campaignArr),
        calendar: ftClockCalendarFromArr(campaignArr),
        runningSinceRaw: sinceStr,
        runningSinceMs: derived ? ftClockParseUtc(sinceStr) : null,
        derived,
        staleCapSeconds: cap > 0 ? cap : 900,
    };
}

export interface CampaignClockDerived {
    running: boolean;
    /** Whole seconds folded into the checkpoint at serverNowMs (0 when paused). */
    elapsedSeconds: number;
    cols: CampaignClockCols;
    formatted: CampaignClockFormatted;
}

/** Derive "now" from a base at the given SERVER-corrected instant (PHP twin: ft_campaign_clock_elapsed_seconds + add). */
export function ftDeriveCampaignClock(base: CampaignClockBase, serverNowMs: number): CampaignClockDerived {
    let elapsed = 0;
    const running = base.derived && base.runningSinceMs !== null;
    if (running && base.runningSinceMs !== null) {
        elapsed = Math.floor((serverNowMs - base.runningSinceMs) / 1000);
        elapsed = Math.max(0, Math.min(base.staleCapSeconds, elapsed));
    }
    const cols = elapsed > 0 ? ftCampaignTimeAddSeconds(base.cols, base.calendar, elapsed) : { ...base.cols };
    return { running, elapsedSeconds: elapsed, cols, formatted: ftFormatCampaignTime(cols, base.calendar) };
}
